import i18n from '@/i18n'
import router from '@/router'
import axios from 'axios'
import * as cookies from 'tiny-cookie'
import { apiV3Segment, sessionCookieName } from '../../shared/constants'
import { DesktopUtils } from '../../utils/desktopsUtils'
import { DirectViewerUtils } from '../../utils/directViewerUtils'
import { ErrorUtils } from '../../utils/errorUtils'
import { DateUtils } from '../../utils/dateUtils'
import { jwtDecode } from 'jwt-decode'

const getDefaultState = () => {
  return {
    viewers: localStorage.viewers ? JSON.parse(localStorage.viewers) : {},
    desktops: [],
    currentTab: 'desktops',
    directViewer: {
      viewersDocumentationUrl: 'https://isard.gitlab.io/isardvdi-docs/user/viewers/viewers/',
      name: '',
      description: '',
      viewers: [],
      state: '',
      shutdown: ''
    },
    desktops_loaded: false,
    viewType: 'grid',
    showStarted: false,
    filters: {
      desktops: ''
    },
    directLink: {
      modalShow: false,
      link: '',
      domainId: '',
      enabled: null
    },
    resetModal: {
      show: false,
      item: {
        id: '',
        action: ''
      }
    },
    desktopModal: {
      show: false,
      type: '',
      item: {
        id: ''
      }
    },
    bastionTargets: [],
    bastionModal: {
      show: false,
      desktop: {},
      bastion: { http: {}, ssh: {} }
    },
    pendingOperations: {} // Track pending desktop operations for button state management
  }
}

const state = getDefaultState()

export default {
  state,
  getters: {
    getDesktops: state => {
      return state.desktops
    },
    getDesktop: state => (id) => {
      return state.desktops.find(d => d.id === id)
    },
    getDesktopsLoaded: state => {
      return state.desktops_loaded
    },
    getViewers: state => {
      return state.viewers
    },
    getViewType: state => {
      return state.viewType
    },
    getShowStarted: state => {
      return state.showStarted
    },
    getDesktopsFilter: state => {
      return state.filters.desktops
    },
    getDirectViewer: state => {
      return state.directViewer
    },
    getCurrentTab: state => {
      return state.currentTab
    },
    getDirectLinkModalShow: state => {
      return state.directLink.modalShow
    },
    getDirectLink: state => {
      return state.directLink.link
    },
    getDirectLinkDomainId: state => {
      return state.directLink.domainId
    },
    getDirectLinkEnabled: state => {
      return state.directLink.enabled
    },
    getResetModal: state => {
      return state.resetModal
    },
    getDesktopModal: state => {
      return state.desktopModal
    },
    getBastionTargets: state => {
      return state.bastionTargets
    },
    getBastionModal: state => {
      return state.bastionModal
    },
    isPendingOperation: (state) => (desktopId) => {
      const pending = state.pendingOperations[desktopId]
      if (!pending) return false

      // Auto-expire after 5 seconds (safety timeout)
      if (Date.now() - pending.timestamp > 5000) {
        return false
      }
      return true
    }
  },
  mutations: {
    resetDesktopsState: (state) => {
      Object.assign(state, getDefaultState())
    },
    resetDirectLinkState: (state) => {
      state.directLink.modalShow = false
      state.directLink.link = ''
      state.directLink.domainId = false
      state.directLink.enabled = null
    },
    setDesktops: (state, desktops) => {
      state.desktops = desktops
      state.desktops_loaded = true
    },
    updateViewers: (state, viewers) => {
      state.viewers = { ...state.viewers, ...viewers }
      localStorage.viewers = JSON.stringify(state.viewers)
    },
    setViewType: (state, type) => {
      state.viewType = type
    },
    toggleShowStarted: (state, type) => {
      state.showStarted = !state.showStarted
    },
    add_desktop: (state, desktop) => {
      state.desktops = [...state.desktops, desktop]
    },
    update_desktop: (state, desktop) => {
      const item = state.desktops.find(d => d.id === desktop.id)
      if (item) {
        Object.assign(item, desktop)
      }
    },
    SET_PENDING_OPERATION: (state, { desktopId, action }) => {
      state.pendingOperations = {
        ...state.pendingOperations,
        [desktopId]: {
          action,
          timestamp: Date.now()
        }
      }
    },
    CLEAR_PENDING_OPERATION: (state, desktopId) => {
      const { [desktopId]: removed, ...rest } = state.pendingOperations
      state.pendingOperations = rest
    },
    remove_desktop: (state, desktop) => {
      const desktopIndex = state.desktops.findIndex(d => d.id === desktop.id)
      if (desktopIndex !== -1) {
        state.desktops.splice(desktopIndex, 1)
      }
    },
    saveDesktopFilter: (state, payload) => {
      state.filters.desktops = payload.filter
    },
    saveDirectViewer: (state, payload) => {
      state.directViewer.name = payload.name
      state.directViewer.description = payload.description
      state.directViewer.viewers = Object.keys(payload.viewers).map((viewer) => {
        return payload.viewers[viewer]
      })
      state.directViewer.state = payload.state
      state.directViewer.jwt = payload.jwt
      state.directViewer.desktopId = payload.desktopId
      state.directViewer.shutdown = payload.shutdown
      state.directViewer.viewersDocumentationUrl = payload.viewersDocumentationUrl
    },
    setDirectViewerErrorState: (state) => {
      state.directViewer.state = 'error'
    },
    setCurrentTab: (state, currentTab) => {
      state.currentTab = currentTab
    },
    setDirectLinkModalShow: (state, directLinkModalShow) => {
      state.directLink.modalShow = directLinkModalShow
    },
    setDirectLink: (state, directLink) => {
      state.directLink.link = directLink
    },
    setDirectLinkDomainId: (state, domainId) => {
      state.directLink.domainId = domainId
    },
    setDirectLinkEnabled: (state, enabled) => {
      state.directLink.enabled = enabled
    },
    setResetModal: (state, resetModal) => {
      state.resetModal = resetModal
    },
    setDesktopModal: (state, desktopModal) => {
      state.desktopModal = desktopModal
    },
    setBastionTargets: (state, bastionTargets) => {
      state.bastionTargets = bastionTargets
    },
    addBastionTarget: (state, target) => {
      state.bastionTargets = [...state.bastionTargets, target]
    },
    updateBastionTarget: (state, target) => {
      const item = state.bastionTargets.find(d => d.id === target.id)
      if (item) {
        Object.assign(item, target)
      }
    },
    removeBastionTarget: (state, target) => {
      const targetIndex = state.bastionTargets.findIndex(d => d.id === target.id)
      if (targetIndex !== -1) {
        state.bastionTargets.splice(targetIndex, 1)
      }
    },
    setBastionModal: (state, data) => {
      state.bastionModal.show = data.show
      const bastion = data.bastion || { http: {}, ssh: {} }
      state.bastionModal.bastion = bastion
      const desktop = data.desktop || {}
      state.bastionModal.desktop = desktop
    }
  },
  actions: {
    resetDesktopsState (context) {
      context.commit('resetDesktopsState')
    },
    resetDirectLinkState (context) {
      context.commit('resetDirectLinkState')
    },
    socket_directviewerUpdate (context, data) {
      context.commit('saveDirectViewer', DirectViewerUtils.parseDirectViewer(JSON.parse(data)))
    },
    socket_desktopAdd (context, data) {
      const desktop = DesktopUtils.parseDesktop(JSON.parse(data))
      context.commit('add_desktop', desktop)
    },
    socket_desktopUpdate (context, data) {
      const desktop = DesktopUtils.parseDesktop(JSON.parse(data))

      // Only update if there are actual changes
      const existingDesktop = context.getters.getDesktop(desktop.id)
      if (existingDesktop) {
        const hasChanges = Object.keys(desktop).some(key =>
          JSON.stringify(existingDesktop[key]) !== JSON.stringify(desktop[key])
        )

        if (hasChanges) {
          context.commit('update_desktop', desktop)
          // Clear pending operation when status update arrives
          // This provides faster feedback than setTimeout
          if (['Started', 'Stopped', 'Failed', 'Verifying'].includes(desktop.status)) {
            context.commit('CLEAR_PENDING_OPERATION', desktop.id)
          }
        }
      } else {
        // Desktop doesn't exist, add it
        context.commit('add_desktop', desktop)
      }
    },
    socket_desktopDelete (context, data) {
      const desktop = JSON.parse(data)
      context.commit('remove_desktop', desktop)
    },
    socket_desktopsQueue (context, data) {
      data = JSON.parse(data)

      for (const [desktopId, queueInfo] of Object.entries(data)) {
        const desktop = context.getters.getDesktop(desktopId)
        if (desktop) {
          desktop.queue = queueInfo.position
          context.commit('update_desktop', desktop)
        }
      }
    },
    loadViewers (context, viewers) {
      context.commit('updateViewers', viewers)
    },
    fetchDesktops (context) {
      axios.get(`${apiV3Segment}/user/desktops`).then(response => {
        context.commit('setDesktops', DesktopUtils.parseDesktops(response.data))
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    createDesktop (_, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.creating-desktop'))

      axios.post(`${apiV3Segment}/nonpersistent`, data, { timeout: 25000 }).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    changeDesktopStatus (context, data) {
      const isFromDeployment = data.deployment || false
      if (isFromDeployment) {
        delete data.deployment
      }
      // Check for pending operation (500ms debounce)
      const pending = context.state.pendingOperations[data.desktopId]
      if (pending && Date.now() - pending.timestamp < 500) {
        console.log('Debounced: Ignoring rapid click for desktop', data.desktopId)
        return Promise.resolve() // Return resolved promise for consistency
      }

      // Set pending state
      context.commit('SET_PENDING_OPERATION', {
        desktopId: data.desktopId,
        action: data.action
      })

      return axios.get(`${apiV3Segment}/desktop/${data.action}/${data.desktopId}`).then(response => {
        context.commit('update_desktop', { id: data.desktopId, state: DesktopUtils.parseState({ state: response.data.status }) })
        // Once the request is successful, we can clear the pending state
        context.commit('CLEAR_PENDING_OPERATION', data.desktopId)
        if (data.action === 'start' && !isFromDeployment) {
          context.dispatch('fetchNotifications', { trigger: 'start_desktop', display: 'modal' })
        }
        return response
      }).catch(e => {
        // Clear immediately on error so user can retry
        context.commit('CLEAR_PENDING_OPERATION', data.desktopId)
        ErrorUtils.handleErrors(e, this._vm.$snotify)
        throw e
      })
    },
    cancelOperation (_, data) {
      this._vm.$snotify.prompt(`${i18n.t('messages.confirmation.cancel-operation')}`, {
        position: 'centerTop',
        buttons: [
          {
            text: `${i18n.t('messages.yes')}`,
            action: () => {
              this._vm.$snotify.clear()
              data.storage.forEach((storage) => {
                axios.put(`${apiV3Segment}/storage/${storage}/abort_operations`).then(() => {
                  ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.cancelling-operation'))
                }).catch(e => {
                  ErrorUtils.handleErrors(e, this._vm.$snotify)
                })
              })
            },
            bold: true
          },
          { text: `${i18n.t('messages.no')}` }
        ],
        placeholder: ''
      })
    },
    resetDesktop (_, data) {
      axios.put(`${apiV3Segment}/direct/${data.token}/${data.action}`).then(response => {
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    openDesktop (context, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.opening-desktop'))

      // Save last viewer selected
      const viewers = {}
      if (data.template) {
        viewers[data.template] = data.viewer
      } else {
        viewers[data.desktopId] = data.viewer
      }
      context.commit('updateViewers', viewers)

      axios.get(`${apiV3Segment}/desktop/${data.desktopId}/viewer/${data.viewer}`).then(response => {
        this._vm.$snotify.clear()

        const el = document.createElement('a')
        if (response.data.kind === 'file') {
          el.setAttribute(
            'href',
              `data:${response.data.mime};charset=utf-8,${encodeURIComponent(response.data.content)}`
          )
          el.setAttribute('download', `${response.data.name}.${response.data.ext}`)
          ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.file-downloaded'), '', false, 1000)
        } else if (response.data.kind === 'browser') {
          if (response.data.protocol === 'rdp') {
            cookies.setCookie('viewerToken', cookies.getCookie(sessionCookieName))
          }
          cookies.setCookie('browser_viewer', response.data.cookie)
          el.setAttribute('href', response.data.viewer)
          el.setAttribute('target', '_blank')
        }
        el.style.display = 'none'
        document.body.appendChild(el)
        el.click()
        document.body.removeChild(el)
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    createNewDesktop (context, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.creating-desktop'), '', true, 1000)

      axios.post(`${apiV3Segment}/persistent_desktop`, data).then(response => {
        context.dispatch('updateBastion', response.data.id)
        router.push({ name: 'desktops' })
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    createNewDesktopFromMedia (_, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.creating-desktop'), '', true, 1000)

      axios.post(`${apiV3Segment}/desktop/from/media`, data).then(response => {
        router.push({ name: 'desktops' })
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    deleteDesktop (context, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.deleting-desktop'))
      const url = data.permanent
        ? `${apiV3Segment}/desktop/${data.id}/permanent`
        : `${apiV3Segment}/desktop/${data.id}`

      axios.delete(url).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    toggleDesktopVisible (context, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t(data.visible ? 'messages.info.making-invisible-desktop' : 'messages.info.making-visible-desktop'), '', true, 1000)
      axios.put(`${apiV3Segment}/deployments/domain/visible/${data.id}`).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    deleteNonpersistentDesktop (_, desktopId) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.deleting-desktop'))

      axios.delete(`${apiV3Segment}/nonpersistent/${desktopId}`).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    updateDesktopsFilter (context, payload) {
      context.commit('saveDesktopFilter', payload)
    },
    setViewType (context, viewType) {
      context.commit('setViewType', viewType)
    },
    toggleShowStarted (context) {
      context.commit('toggleShowStarted')
    },
    navigate (context, path) {
      router.push({ name: path })
    },
    getDirectViewers (context, payload) {
      axios.get(`${apiV3Segment}/direct/docs`).then(response => {
        const config = { ...context.getters.getConfig }
        config.viewersDocumentationUrl = response.data.viewers_documentation_url
        context.commit('setConfig', config)
      })
      return axios.get(`/api/v3/direct/${payload.token}`).then(response => {
        context.commit('saveDirectViewer', DirectViewerUtils.parseDirectViewer(response.data))
      }).catch(e => {
        context.commit('setDirectViewerErrorState')
        // If the error is that the desktop needs booking format he given time to the users local
        if (e.response.data.description_code === 'desktop_not_booked_until') {
          e.response.data.params.start = DateUtils.utcToLocalTime(e.response.data.params.start)
        }
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    openDirectViewerDesktop (_, payload) {
      const token = router.currentRoute.params.pathMatch
      if (token) {
        axios.post(`/api/v3/direct/${token}/viewer/${payload.kind}-${payload.protocol}`)
          .catch(() => {})
      }

      const el = document.createElement('a')

      if (payload.kind === 'file') {
        el.setAttribute(
          'href',
            `data:${payload.mime};charset=utf-8,${encodeURIComponent(payload.content)}`
        )
        el.setAttribute('download', `${payload.name}.${payload.ext}`)
      } else if (payload.kind === 'browser') {
        const exp = payload.protocol === 'rdp' ? jwtDecode(payload.cookie).web_viewer.exp * 1000 : JSON.parse(atob(decodeURIComponent(payload.cookie))).web_viewer.exp * 1000
        cookies.setCookie('browser_viewer', payload.cookie, { expires: exp })

        const url = new URL(payload.viewer)
        url.searchParams.append('direct', '1')

        el.setAttribute('href', url.toString())
      }

      el.style.display = 'none'
      document.body.appendChild(el)
      el.click()
      document.body.removeChild(el)
    },
    updateCurrentTab (context, currentTab) {
      if (currentTab === 'sharedTemplates' && !context.getters.getSharedTemplatesLoaded) {
        context.dispatch('fetchAllowedTemplates', 'shared')
      }
      context.commit('setCurrentTab', currentTab)
    },
    fetchDirectLink (context, domainId) {
      axios.get(`${apiV3Segment}/desktop/jumperurl/${domainId}`).then(response => {
        context.commit('setDirectLinkDomainId', domainId)
        context.commit('setDirectLinkEnabled', !!response.data.jumperurl)
        context.commit('setDirectLink', response.data.jumperurl ? `${location.protocol}//${location.host}/vw/${response.data.jumperurl}` : '')
        context.dispatch('directLinkModalShow', true)
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    directLinkModalShow (context, show) {
      context.commit('setDirectLinkModalShow', show)
    },
    toggleDirectLink (context, data) {
      axios.put(`${apiV3Segment}/desktop/jumperurl_reset/${data.domainId}`, { disabled: data.disabled }).then(response => {
        context.commit('setDirectLink', response.data ? `${location.protocol}//${location.host}/vw/${response.data}` : '')
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    editDesktopReservables (context, data) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.editing'))
      return axios.put(`${apiV3Segment}/domain/reservables/${data.id}`, data).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    updateResetModal (context, data) {
      context.commit('setResetModal', data)
    },
    resetResetModal (context) {
      context.commit('setResetModal', {
        show: false,
        item: {
          id: '',
          action: ''
        }
      })
    },
    updateDesktopModal (context, data) {
      context.commit('setDesktopModal', data)
    },
    resetDesktopModal (context) {
      context.commit('setDesktopModal', {
        show: false,
        type: '',
        item: {
          id: ''
        }
      })
    },
    recreateDesktop (context, data) {
      axios.post(`${apiV3Segment}/domain/${data.id}/recreate_disk`).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    fetchBastionTargets (context) {
      const config = context.getters.getConfig
      if (config.canUseBastion === true) {
        axios.get(`${apiV3Segment}/bastion_targets`).then(response => {
          context.commit('setBastionTargets', response.data)
        }).catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
      }
    },
    bastionModalShow (context, data) {
      context.commit('setBastionModal', data)
    },
    updateBastionAuthorizedKeys (context, data) {
      axios.put(`${apiV3Segment}/desktop/${data.desktop_id}/bastion/authorized_keys`, { authorized_keys: data.ssh.authorized_keys }).then(response => {
        ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.authorized-ssh-keys-updated'))
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    updateBastionDomainName (context, data) {
      axios.put(`${apiV3Segment}/desktop/${data.desktop_id}/bastion/domain`, { domain: data.domain || null }).then(response => {
        ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.bastion-domain-updated'))
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    updateBastionDomains (context, data) {
      axios.put(`${apiV3Segment}/desktop/${data.desktop_id}/bastion/domains`, { domains: data.domains || [] }).then(response => {
        ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.bastion-domains-updated'))
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    verifyBastionDomain (context, data) {
      return axios.post(`${apiV3Segment}/desktop/${data.desktop_id}/bastion/domain/verify`, { domain: data.domain })
        .then(response => {
          return { success: true }
        }).catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
          return { success: false, error: e }
        })
    },
    socket_targetsAdd (context, data) {
      context.commit('addBastionTarget', JSON.parse(data))
    },
    socket_targetsUpdate (context, data) {
      context.commit('updateBastionTarget', JSON.parse(data))
    },
    socket_targetsDelete (context, data) {
      context.commit('removeBastionTarget', JSON.parse(data))
    },
    extendDesktopTimeout (context, desktopId) {
      return axios.put(`${apiV3Segment}/desktop/${desktopId}/extend-timeout`).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    }
  }
}
